import { Chevron } from "@/icons";
import { cn } from "@/lib/cn";
import { cva, type VariantProps } from "class-variance-authority";
import {
  forwardRef,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type ReactNode,
} from "react";
import { Link, type LinkProps } from "react-router-dom";
import { Icon } from "./Icon";
import { SHEET_GUTTER } from "./Sheet";
import { SurfaceProvider } from "./Surface";
import { Text, type TextTone } from "./Typography";
import { useElementRef } from "./use-element-ref";

/**
 * Classes of `ListRow`: a card tile, a plain row or a menu row (drawer entry: wider gap, primary-tinted hover pill);
 * `size="lg"` is a roomy pick list row (category picker): `py-4` and an `lg` medium title; `inset` pads a plain row
 * with the sheet gutter (a full-bleed list in a flush `SheetBody`); interactive rows add hover (pointer devices only,
 * none on the `aria-current` row) and focus.
 */
export const listRowVariants = cva("flex w-full items-center text-left", {
  variants: {
    variant: {
      card: "gap-2 rounded-card bg-card p-4 shadow-card",
      plain: "gap-2 py-3",
      menu: "gap-3 rounded-card py-3 pr-3",
    },
    size: {
      md: "",
      lg: "py-4",
    },
    inset: {
      true: SHEET_GUTTER,
      false: "",
    },
    interactive: {
      true: [
        "select-none transition-colors ease-standard",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2",
        "disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50",
      ],
      false: "",
    },
  },
  compoundVariants: [
    {
      variant: ["card", "plain"],
      interactive: true,
      class: "duration-fast [@media(hover:hover)]:hover:bg-gray-scale-100 aria-[current=true]:hover:bg-transparent",
    },
    { variant: "menu", interactive: true, class: "duration-base hover:bg-primary/10" },
  ],
  defaultVariants: { variant: "card", size: "md", inset: false, interactive: false },
});

type RowContent = Omit<VariantProps<typeof listRowVariants>, "interactive"> & {
  leading?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  trailing?: "chevron" | ReactNode;
  tone?: TextTone;
  /** Title weight; defaults to `medium` for `menu`/`lg` rows and `semibold` otherwise (`bold` marks the current pick). */
  titleWeight?: "medium" | "semibold" | "bold";
  className?: string;
};

type ButtonRow = RowContent & { as: "button" } & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "title" | "className">;
type LinkRow = RowContent & { as: "link"; disabled?: boolean } & Omit<LinkProps, "title" | "className">;
type DivRow = RowContent & { as?: "div" } & Omit<HTMLAttributes<HTMLDivElement>, "title" | "className">;

/** Props of `ListRow`: `as="button"` takes button props, `as="link"` takes react-router `to`. */
export type ListRowProps = ButtonRow | LinkRow | DivRow;

function RowBody({ leading, title, description, trailing, tone = "muted", titleWeight, variant, size }: RowContent) {
  const body = (
    <>
      {leading && <span className="flex shrink-0 items-center">{leading}</span>}
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <Text as="span" size={size === "lg" ? "lg" : "base"} weight={titleWeight ?? (variant === "menu" || size === "lg" ? "medium" : "semibold")} tone={tone}>
          {title}
        </Text>
        {description && (
          <Text as="span" size="sm" tone="subtle">
            {description}
          </Text>
        )}
      </span>
      {trailing === "chevron" ? <Icon as={Chevron} size="sm" tone="muted" /> : trailing}
    </>
  );
  return variant === "plain" || variant === "menu" ? body : <SurfaceProvider surface="card">{body}</SurfaceProvider>;
}

/** Row with leading media, title, description and a trailing chevron or slot; renders as button, link or div. */
export const ListRow = forwardRef<HTMLElement, ListRowProps>(function ListRow(props, ref) {
  const elementRef = useElementRef(ref);

  if (props.as === "button") {
    const { as: _as, leading, title, description, trailing, tone, titleWeight, variant, size, inset, className, type = "button", ...rest } = props;
    return (
      <button
        {...rest}
        ref={elementRef}
        type={type}
        className={cn(listRowVariants({ variant, size, inset, interactive: true }), className)}
      >
        <RowBody leading={leading} title={title} description={description} trailing={trailing} tone={tone} titleWeight={titleWeight} variant={variant} size={size} />
      </button>
    );
  }

  if (props.as === "link") {
    const { as: _as, leading, title, description, trailing, tone, titleWeight, variant, size, inset, className, disabled, ...rest } = props;
    return (
      <Link
        {...rest}
        ref={elementRef}
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? -1 : rest.tabIndex}
        className={cn(listRowVariants({ variant, size, inset, interactive: true }), className)}
      >
        <RowBody leading={leading} title={title} description={description} trailing={trailing} tone={tone} titleWeight={titleWeight} variant={variant} size={size} />
      </Link>
    );
  }

  const { as: _as, leading, title, description, trailing, tone, titleWeight, variant, size, inset, className, ...rest } = props;
  return (
    <div {...rest} ref={elementRef} className={cn(listRowVariants({ variant, size, inset, interactive: false }), className)}>
      <RowBody leading={leading} title={title} description={description} trailing={trailing} tone={tone} titleWeight={titleWeight} variant={variant} size={size} />
    </div>
  );
});
